import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { parseISO, format } from "date-fns";
import { Edit, Trash2, EyeIcon as Eye } from 'lucide-react';
import UserService from "@/services/user.service";
import { useDeleteUser } from "@/hooks/useUsers";
import { useTranslation } from "react-i18next";

interface User {
    _id: string;
    full_name: string;
    email_address: string;
    phone_number: string;
    role: string;
    status: string;
    createdAt: string;
}

interface UserTableProps {
    users: User[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export default function UserTable({ users, currentPage, totalPages, onPageChange }: UserTableProps) {
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const { mutate: deleteUser, isLoading: isDeleting } = useDeleteUser();
    const { t } = useTranslation();

    const handleDelete = (id: string) => {
        deleteUser(id, {
            onSuccess: () => {
                setSelectedUser(null);
            },
        });
    };

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <div className="min-w-[1102px]">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Name")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Email")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Phone Number")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Created At")}
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="font-bold px-5 py-3 text-gray-700 dark:text-gray-400 text-start"
                                >
                                    {t("Actions")}
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {users.map((user) => (
                                <TableRow key={user._id}>
                                    <TableCell className="px-5 py-4 sm:px-6 text-start">
                                        <div className="flex items-center gap-3">
                                            <div>
                                                <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                    {user.full_name}
                                                </span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {user.email_address}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {user.phone_number || 'N/A'}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {/* <Badge
                                            size="sm"
                                            color={user.status === "Active" ? "success" : "error"}
                                        >
                                        </Badge> */}
                                        {format(parseISO(user.createdAt), "MM/dd/yyyy hh:mm a")}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <button
                                            onClick={() => { window.location.href = "/list-users/" + user._id }}
                                            className="p-2 text-yellow-500 hover:bg-yellow-100 rounded-md"
                                        >
                                            <Eye className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => { window.location.href = "/edit-user/" + user._id }}
                                            className="p-2 text-blue-500 hover:bg-blue-100 rounded-md"
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => setSelectedUser(user)}
                                            className="p-2 text-red-500 hover:bg-red-100 rounded-md"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                        {
                                            selectedUser && (
                                                <>
                                                    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-20">
                                                        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
                                                            <h2 className="text-xl font-semibold mb-4">{t('Confirm Deletion')}</h2>
                                                            <p className="text-gray-600 mb-6">{t('Are you sure you want to delete this user')} <b>{selectedUser.full_name}</b>? {t('This action is irreversible.')}</p>
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    onClick={() => setSelectedUser(null)}
                                                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                                                >
                                                                    {t("Cancel")}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(selectedUser._id)}
                                                                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                                                                >
                                                                    {isDeleting ? (
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                                            </svg>
                                                                            {t("Deleting")} ...
                                                                        </div>
                                                                    ) : (
                                                                        <>{t("Delete")}</>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        }

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.05]">
                <div className="flex flex-1 justify-center">
                    <nav className="relative z-0 inline-flex -space-x-px rounded-md" aria-label="Pagination">
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => onPageChange(page)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                                    ? "z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                    }`}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );
}